import { ActionIcon, Badge, Box, Group, MultiSelect, Select, Text, Tooltip } from '@mantine/core'
import { IconEye, IconQuestionMark, IconThumbDown, IconThumbUp } from '@tabler/icons-react'
import dayjs from 'dayjs'
import utcPlugin from 'dayjs/plugin/utc'

dayjs.extend(utcPlugin)
import { useMemo } from 'react'

import type { FeedbackAnnotation, FeedbackTag, FeedbackType } from '@/services/api/api.types'
import { createContentModuleFilterOptions, createTopicFilterOptions } from '@/utils/contentModuleUtils'
import type { TableColumnDef } from '@/utils/table'

import { CommentFilterInput, DateRangeFilterInput } from './FeedbackFilterInputs'
import { FILTER_STYLES } from './filterStyles'

interface CreateColumnsParams {
  topicsData?: Array<{ id: string; displayName: string }>
  contentModulesData?: Record<string, number> // byContentModule object with "title_id" -> count mapping
  byTopicData?: Record<string, number> // byTopic stats object with topic ID -> count mapping
  tagsData?: FeedbackTag[]
  onViewDetails: (feedback: FeedbackAnnotation) => void
  topicsEnabled?: boolean
}

/** Column ids, which are also the keys `useFeedbackFilters` reads filters and sorting under. */
export const FEEDBACK_COLUMN = {
  date: 'createdAt',
  module: 'taskId',
  rating: 'annotationKind',
  tags: 'tagIds',
  comment: 'userFeedback',
  topics: 'cmTopics',
} as const satisfies Record<string, keyof FeedbackAnnotation>

/** What the range date input writes into column filter state: "YYYY-MM-DD". */
export type DateRangeFilter = [string | null, string | null]

const ratingOptions: Array<{ value: FeedbackType; label: string }> = [
  { value: 'thumbs-up', label: 'Positive' },
  { value: 'thumbs-down', label: 'Negative' },
  { value: 'unknown', label: 'None' },
]

export function createFeedbackTableColumns({
  topicsData = [],
  contentModulesData = {},
  byTopicData = {},
  tagsData,
  onViewDetails,
  topicsEnabled = false,
}: CreateColumnsParams): TableColumnDef<FeedbackAnnotation>[] {
  const tagOptions = (tagsData || [])
    .filter((tag, index, self) => {
      const isFirst = index === self.findIndex(t => t.id === tag.id)
      if (!isFirst) {
        console.warn(`[FeedbackTable] Duplicate tag id detected: "${tag.id}"`)
      }
      return isFirst
    })
    .map(tag => ({ value: tag.id, label: tag.name }))

  const columns: TableColumnDef<FeedbackAnnotation>[] = [
    {
      header: 'Date',
      accessorKey: FEEDBACK_COLUMN.date,
      sortDescFirst: true,
      size: 250,
      meta: {
        grow: false,
        filter: ({ value, onChange }) => (
          <DateRangeFilterInput value={value as DateRangeFilter | undefined} onChange={onChange} />
        ),
      },
      cell: ({ getValue }) => <Text size="sm">{dayjs.utc(getValue<string>()).local().format('MMM D, YYYY')}</Text>,
    },
    {
      header: 'Module',
      accessorKey: FEEDBACK_COLUMN.module,
      size: 180,
      meta: {
        filter: ({ value, onChange }) => (
          <MultiSelect
            aria-label="Filter by modules"
            data={createContentModuleFilterOptions(contentModulesData)}
            placeholder="Filter by modules"
            value={(value as string[] | undefined) ?? []}
            onChange={next => onChange(next.length > 0 ? next : undefined)}
            clearable
            searchable
            size="sm"
            styles={FILTER_STYLES}
          />
        ),
      },
      cell: ({ row }) => {
        const contentModuleTitle = row.original.contentModuleTitle
        return contentModuleTitle ? (
          <Tooltip label={contentModuleTitle} position="top-start" withArrow>
            <Text size="sm" lineClamp={2}>
              {contentModuleTitle}
            </Text>
          </Tooltip>
        ) : (
          <Text c="gray.6" fs="italic" size="sm">
            No module
          </Text>
        )
      },
    },
    {
      header: 'Rating',
      accessorKey: FEEDBACK_COLUMN.rating,
      size: 170,
      meta: {
        grow: false,
        filter: ({ value, onChange }) => (
          <Select
            aria-label="Filter by rating"
            data={ratingOptions}
            placeholder="Filter by rating"
            value={(value as string | undefined) ?? null}
            onChange={next => onChange(next ?? undefined)}
            clearable
            size="sm"
            styles={FILTER_STYLES}
          />
        ),
      },
      cell: ({ getValue }) => {
        const value = getValue<FeedbackType>()
        const label = value === 'thumbs-up' ? 'Positive' : value === 'thumbs-down' ? 'Negative' : 'Unknown'
        const icon =
          value === 'thumbs-up' ? (
            <IconThumbUp size={20} color="green" />
          ) : value === 'thumbs-down' ? (
            <IconThumbDown size={20} color="red" />
          ) : (
            <IconQuestionMark size={20} color="gray" />
          )

        return (
          <Group justify="center">
            <Tooltip label={label} position="top" withArrow>
              {icon}
            </Tooltip>
          </Group>
        )
      },
    },
    {
      header: 'Tag',
      accessorKey: FEEDBACK_COLUMN.tags,
      size: 200,
      // The API sorts by a field the records don't have.
      enableSorting: false,
      meta: {
        grow: false,
        filter: ({ value, onChange }) => (
          <MultiSelect
            aria-label="Filter by tags"
            data={tagOptions}
            placeholder="Filter by tags"
            value={(value as string[] | undefined) ?? []}
            onChange={next => onChange(next.length > 0 ? next : undefined)}
            clearable
            searchable
            size="sm"
            styles={FILTER_STYLES}
          />
        ),
      },
      cell: ({ row }) => {
        const tags = row.original.tagIds
        return tags && tags.length > 0 ? (
          <Group gap="xs" wrap="wrap">
            {tags.map((tag, index) => (
              <Badge key={tag.tagId || index} variant="light" size="sm">
                {tag.tagName}
              </Badge>
            ))}
          </Group>
        ) : (
          <Text c="gray.6" fs="italic" size="sm">
            No tag
          </Text>
        )
      },
    },
    {
      header: 'Comment',
      accessorKey: FEEDBACK_COLUMN.comment,
      size: 180,
      enableSorting: false,
      meta: {
        filter: ({ value, onChange }) => <CommentFilterInput value={value as string | undefined} onChange={onChange} />,
      },
      cell: ({ getValue }) => {
        const value = getValue<string | null>()
        return value ? (
          <Tooltip label={value} position="top-start" withArrow>
            <Text size="sm" lineClamp={2}>
              {value}
            </Text>
          </Tooltip>
        ) : (
          <Text c="gray.6" fs="italic" size="sm">
            No comment
          </Text>
        )
      },
    },
    {
      id: 'detail',
      header: 'Detail',
      size: 90,
      enableSorting: false,
      meta: { grow: false },
      cell: ({ row }) => (
        <Box style={{ textAlign: 'center' }}>
          <ActionIcon
            variant="subtle"
            color="blue"
            onClick={() => onViewDetails(row.original)}
            size="sm"
            aria-label="View feedback details">
            <IconEye size={16} aria-hidden="true" />
          </ActionIcon>
        </Box>
      ),
    },
  ]

  if (topicsEnabled) {
    columns.splice(1, 0, {
      header: 'Topic',
      accessorKey: FEEDBACK_COLUMN.topics,
      size: 170,
      // The API sorts topics by id, not by name.
      enableSorting: false,
      meta: {
        grow: false,
        filter: ({ value, onChange }) => (
          <MultiSelect
            aria-label="Filter by topics"
            data={createTopicFilterOptions(byTopicData, topicsData)}
            placeholder="Filter by topics"
            value={(value as string[] | undefined) ?? []}
            onChange={next => onChange(next.length > 0 ? next : undefined)}
            clearable
            searchable
            size="sm"
            styles={FILTER_STYLES}
          />
        ),
      },
      cell: ({ row }) => {
        const cmTopics = row.original.cmTopics
        const topicName = row.original.topicName

        // Drop topic IDs not in the sponsor list (e.g. help modules) so UUIDs aren't shown
        const topicsToDisplay =
          cmTopics && cmTopics.length > 0
            ? cmTopics
                .map(topicId => topicsData.find(t => t.id === topicId)?.displayName)
                .filter((displayName): displayName is string => Boolean(displayName))
            : topicName
              ? [topicName]
              : []

        return topicsToDisplay.length > 0 ? (
          <Group gap="xs" wrap="wrap">
            {topicsToDisplay.map((topic, index) => (
              <Text
                key={index}
                size="sm"
                style={{
                  backgroundColor: 'var(--mantine-color-blue-1)',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  color: 'var(--mantine-color-blue-7)',
                  display: 'inline-block',
                }}>
                {topic}
              </Text>
            ))}
          </Group>
        ) : (
          <Text c="gray.6" fs="italic" size="sm">
            No topic
          </Text>
        )
      },
    })
  }

  return columns
}

export function useFeedbackTableColumns(params: CreateColumnsParams) {
  const { topicsData, contentModulesData, byTopicData, tagsData, onViewDetails, topicsEnabled } = params
  return useMemo(
    () =>
      createFeedbackTableColumns({
        topicsData,
        contentModulesData,
        byTopicData,
        tagsData,
        onViewDetails,
        topicsEnabled,
      }),
    [topicsData, contentModulesData, byTopicData, tagsData, onViewDetails, topicsEnabled],
  )
}
